import { PencilIcon } from "@heroicons/react/24/solid";
import { get } from "lodash";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { useAppSelector } from "../../app/hooks";
import { FormInput } from "../../components/Form/FormInput";
import { FormLabel } from "../../components/Form/FormLabel";
import { getAuthUserInfo } from "../../features/userLogin/userLoginSlice";
import { UserSidebar } from "./UserSidebar";
import { AvoRedApp } from "../../components/Layout/AvoRedApp";
import { CUSTOMER_QUERY } from "../../graphql/queries/categoryQuery";

const customerUpdate = `
    mutation CustomerUpdate(
      $name: String
      $phone: String
    ) {
      customerUpdate(   
          name: $name
          phone: $phone
      ) {
          id
          name
          phone
          email
          created_at
          updated_at
      }
    }
`;

export const EditProfile = () => {
  const currentUserInfo = useAppSelector(getAuthUserInfo);
  const [{ fetching, data: customerData }] = useQuery({
    query: CUSTOMER_QUERY,
  });
  const [, customerUpdateMutation] = useMutation(customerUpdate);

  const customer = get(customerData, "customerQuery");
  const customerGroup = customer?.group;
  const branches = customer?.branches || [];

  // Use customer data if available, fallback to currentUserInfo
  const displayName = customer?.name || currentUserInfo.name;
  const displayEmail = customer?.email || currentUserInfo.email;
  const displayPhone = customer?.phone || currentUserInfo.phone || "";

  const [name, setName] = useState(displayName);
  const [emailName] = useState(displayEmail);
  const [phone, setPhone] = useState(displayPhone);

  const nameOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  const phoneOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
  };

  const navigate = useNavigate();

  const submitHandle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables: any = {};
    if (name) {
      variables.name = name;
    }
    if (phone) {
      variables.phone = phone;
    }
    const result = await customerUpdateMutation(variables);

    const customerID = get(result, "data.customerUpdate.id");

    if (customerID) {
      navigate("/user/profile");
    }
  };

  // Update form values when customer data loads
  React.useEffect(() => {
    if (customer) {
      setName(customer.name || currentUserInfo.name);
      setPhone(customer.phone || currentUserInfo.phone || "");
    }
  }, [customer, currentUserInfo.name, currentUserInfo.phone]);

  return (
    <AvoRedApp>
      {fetching ? (
        <div className="min-h-screen bg-gray-50">
          {/* Breadcrumb Skeleton */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-4 text-gray-300">/</div>
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </nav>

          {/* Content Skeleton */}
          <section className="bg-gray-50 py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                <div className="space-y-4">
                  <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <>
          {/* Breadcrumb Navigation */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2 text-sm overflow-x-auto">
                <Link
                  to="/"
                  className="text-gray-500 hover:text-indigo-600 transition-colors duration-200 flex items-center whitespace-nowrap"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Trang chủ
                </Link>
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-gray-500 whitespace-nowrap">Hồ sơ</span>
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-gray-900 font-semibold truncate max-w-xs md:max-w-md">
                  Thông tin tài khoản
                </span>
              </div>
            </div>
          </nav>

          <div className="bg-white shadow">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Thông tin tài khoản
            </h1>
              <p className="mt-2 text-gray-600">
                Quản lý thông tin cá nhân và tài khoản của bạn
              </p>
            </div>
          </div>

          <section className="bg-gray-50 py-8 md:py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="grid gap-8 lg:grid-cols-4 lg:gap-12">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <UserSidebar user={currentUserInfo} />
                  </div>
                </div>

                {/* Main Content */}
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Form Content */}
                    <form className="p-6 lg:p-8" onSubmit={submitHandle}>
                      <div className="space-y-6">
                        {/* Personal Information Section */}
                        <div>
                          <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
                            <svg
                              className="w-6 h-6 text-blue-600 mr-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <h2 className="text-xl font-bold text-gray-900">
                              Thông tin cá nhân
                            </h2>
                          </div>

                          <div className="grid gap-6 md:grid-cols-2">
                            {/* Name Field */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                                <FormLabel forId="name" labelText="Họ và tên" />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  value={name}
                                  id="name"
                                  type="text"
                                  setOnChange={nameOnChange}
                                  placeholder="Nhập họ và tên"
                                />
                              </div>
                            </div>

                            {/* Email Field */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                                <FormLabel
                                  forId="email"
                                  labelText="Địa chỉ Email"
                                />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  value={emailName}
                                  disabled={true}
                                  id="email"
                                  type="email"
                                  placeholder="Địa chỉ Email"
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                  Email không thể thay đổi
                                </p>
                              </div>
                            </div>

                            {/* Phone Field */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                                <FormLabel
                                  forId="phone"
                                  labelText="Số điện thoại"
                                />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  value={phone}
                                  id="phone"
                                  type="tel"
                                  setOnChange={phoneOnChange}
                                  placeholder="Nhập số điện thoại"
                                />
                              </div>
                            </div>

                            {/* Customer Group */}
                            {customerGroup && (
                              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                                <div className="flex items-center mb-2">
                                  <svg
                                    className="w-5 h-5 text-blue-500 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                  </svg>
                                  <label className="text-sm font-medium text-blue-700">
                                    Khách hàng
                                  </label>
                                </div>
                                <p className="text-lg font-bold text-blue-900">
                                  {customerGroup.name}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Branches Section */}
                        {branches.length > 0 && (
                          <div>
                            <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
                              <svg
                                className="w-6 h-6 text-blue-600 mr-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                              <h2 className="text-xl font-bold text-gray-900">
                                Chi nhánh ({branches.length})
                              </h2>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                              {branches.map((branch: any) => (
                                <div
                                  key={branch.id}
                                  className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-blue-300 transition-colors duration-200"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center mb-2">
                                        <svg
                                          className="w-5 h-5 text-gray-400 mr-2"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                          />
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                          />
                                        </svg>
                                        <h3 className="text-base font-bold text-gray-900">
                                          {branch.name}
                                        </h3>
                                      </div>
                                      {branch.address && (
                                        <p className="text-sm text-gray-600 mt-1">
                                          {branch.address}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Submit Button */}
                        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                          <Link
                            to="/user/profile"
                            className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
                          >
                            Hủy
                          </Link>
                          <button
                            type="submit"
                            className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg"
                          >
                            <PencilIcon className="w-5 h-5" />
                            <span>Cập nhật hồ sơ</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </AvoRedApp>
  );
};
